// Classic Home under "All divisions" (#710, #583 DECISIONS 6): one compact
// table per division (top 4, in admin order; the first 4 divisions, then "See
// all standings"), never one merged table and no carousel, in the card look of Home's standings (SwipeableStandings). Each
// division links to its full table on Schedule.
//
// Rendered only with 2+ live divisions; until the tables are in (or if they
// fail) the section is today's carousel, so a league without divisions is
// pixel-identical and never requests /standings/divisions.

import { Link, useNavigate } from 'react-router-dom'
import type { FeaturedItem } from '@/services/api'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'
import { useTeamDisplay } from '@/utils/teamDisplay'
import { useDivisionStandings } from '../../../core/data/useDivisionStandings'
import { sf } from '../../../core/features/sf'
import { useLabels } from '../../../core/labels'
import SwipeableStandings, { StandingsTable } from './SwipeableStandings'
import { asFeatured, seasonLabel } from './divisionStandingsModel'
import { seasonWithoutDivision } from './DivisionPills'

/** Rows per division on Home; the full table is on Schedule. */
export const HOME_DIVISION_ROWS = 4
/** At most this many divisions on Home; with more, "See all standings" opens Schedule's stacked tables. */
export const HOME_DIVISIONS_MAX = 4

export default function HomeDivisionStandings({ featured, loading, linkSchedule }: {
  /** Today's carousel items: shown while the tables load, and if they fail. */
  featured: FeaturedItem[]
  loading: boolean
  /** The league's Schedule section is on ("Full table" links there). */
  linkSchedule: boolean
}) {
  const t = useLabels()
  const msg = useT(siteCoreMessages).standings
  const navigate = useNavigate()
  const teamName = useTeamDisplay()
  const { status, tables } = useDivisionStandings(true, { all: true })

  if (status === 'loading') return <SwipeableStandings featured={[]} loading />
  if (status !== 'ready' || tables.length === 0) return <SwipeableStandings featured={featured} loading={loading} />

  return (
    <div className="bg-bg-secondary/35 backdrop-blur-md border border-secondary/20 px-4 py-4" {...sf('home.section.standings')}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 id="standings" className="text-base sm:text-lg font-['Montserrat'] font-black text-white uppercase tracking-tight">
          {t.term('standings')}
        </h3>
        <span className="text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold">{t.term('allDivisions')}</span>
      </div>
      <div className="space-y-5">
        {tables.slice(0, HOME_DIVISIONS_MAX).map((table) => {
          const season = seasonLabel(table.season)
          const id = `standings-d${table.division.id}`
          return (
            <section key={table.division.id} aria-labelledby={id}>
              <div className="flex items-center justify-between gap-3 mb-2 min-h-[44px]">
                <div className="flex min-w-0 items-baseline gap-2">
                  <h4 id={id} className="truncate text-sm font-['Montserrat'] font-black text-white uppercase tracking-tight">{table.division.name}</h4>
                  {season && (
                    <span className="flex-none text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold">{seasonWithoutDivision(season, table.division.name)}</span>
                  )}
                </div>
                {linkSchedule && (
                  <Link
                    to={`/schedule?division=${encodeURIComponent(table.division.slug)}`}
                    className="flex-none inline-flex items-center min-h-[44px] text-[11px] uppercase tracking-[0.22em] font-bold text-primary hover:underline underline-offset-4"
                  >
                    {msg.fullTable} ›<span className="sr-only">: {table.division.name}</span>
                  </Link>
                )}
              </div>
              {table.rows.length > 0 ? (
                <StandingsTable
                  featured={asFeatured(table, HOME_DIVISION_ROWS)}
                  isBracketTournament={false}
                  onTeamClick={(teamId) => navigate(`/teams/${teamId}`)}
                  teamName={teamName}
                  caption={`${table.division.name}${season ? `, ${season}` : ''}`}
                  fullHeight
                />
              ) : (
                <div className="bg-bg-primary/12 border border-secondary/30 p-4 text-center text-white/60 text-sm">{msg.noData}</div>
              )}
            </section>
          )
        })}
      </div>
      {tables.length > HOME_DIVISIONS_MAX && linkSchedule && (
        <div className="mt-3 text-right">
          <Link
            to="/schedule?division=all"
            className="inline-flex items-center min-h-[44px] text-[11px] uppercase tracking-[0.22em] font-bold text-primary hover:underline underline-offset-4"
          >
            {msg.seeAll} ›
          </Link>
        </div>
      )}
    </div>
  )
}
